import React, { useState, useEffect, useRef, useMemo } from 'react'
import Modal from './Modal'
import {
  createResidentInvite,
  bulkResidentInvite,
  listResidentInvites,
  parseResidentCSV,
} from '../services/onboardingService'
import { detectPhoneRegion, getPhoneExample, getPhonePrefix, getPhoneValidationMessage, normalizeEmail, normalizePhone, validateEmail, validatePersonName, validatePhone } from '../utils/validation'

const VEHICLE_MAKES_BY_REGION = {
  IN: ['Maruti Suzuki', 'Hyundai', 'Tata', 'Mahindra', 'Toyota', 'Honda', 'Kia', 'Renault', 'MG', 'Skoda', 'Volkswagen', 'Bajaj', 'Hero', 'TVS', 'Royal Enfield', 'Yamaha', 'Other'],
  US: ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'Nissan', 'Hyundai', 'Kia', 'Jeep', 'Subaru', 'Tesla', 'BMW', 'Mercedes-Benz', 'Audi', 'Lexus', 'Harley-Davidson', 'Other'],
}

const VEHICLE_TYPES = [
  { value: 'TWO_WHEELER', label: 'Two Wheeler' },
  { value: 'FOUR_WHEELER', label: 'Four Wheeler' },
  { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
]

const RESIDENT_TYPES = [
  { value: 'RESIDENT_TENANT', label: 'Resident Tenant' },
  { value: 'RESIDENT_OWNER', label: 'Resident Owner' },
  { value: 'NON_RESIDING_OWNER', label: 'Non Residing Owner' },
]

const currentYear = new Date().getFullYear()

function normalizeVehicleType(value) {
  const s = (value || '').toString().trim().toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
  if (s === '2W' || s === 'TWO_WHEELER') return 'TWO_WHEELER'
  if (s === '4W' || s === 'FOUR_WHEELER') return 'FOUR_WHEELER'
  if (s === 'NA' || s === 'N_A' || s === 'NOT_APPLICABLE') return 'NOT_APPLICABLE'
  return ''
}

function isVehicleNotApplicable(value) {
  return normalizeVehicleType(value) === 'NOT_APPLICABLE'
}

function normalizeVehicleNumber(value) {
  return (value || '').toString().trim().toUpperCase().replace(/\s+/g, ' ')
}

function validateVehicleNumber(value) {
  return /^[A-Z0-9\- ]{6,15}$/.test(normalizeVehicleNumber(value))
}

function normalizeVehicleText(value) {
  return (value || '').toString().trim()
}

function normalizeVehicleYear(value) {
  return (value || '').toString().trim()
}

function getInviteValue(invite, ...keys) {
  for (const key of keys) {
    const value = invite?.[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function normalizeBulkResults(results) {
  if (Array.isArray(results)) return results
  if (results && Array.isArray(results.results)) return results.results
  return []
}

function normalizeResidentTypeValue(value) {
  const normalized = (value || '').toString().trim().toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
  if (normalized === 'RESIDENT_TENANT') return 'RESIDENT_TENANT'
  if (normalized === 'RESIDENT_OWNER') return 'RESIDENT_OWNER'
  if (normalized === 'NON_RESIDING_OWNER' || normalized === 'NON_RESIDENT_OWNER') return 'NON_RESIDING_OWNER'
  return 'RESIDENT_OWNER'
}

function getResidentTypeLabel(invite) {
  const residentType = getInviteValue(invite, 'RESIDENT_TYPE', 'residentType', 'resident_type').toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
  if (residentType === 'RESIDENT_TENANT') return 'Resident Tenant'
  if (residentType === 'RESIDENT_OWNER') return 'Resident Owner'
  if (residentType === 'NON_RESIDING_OWNER' || residentType === 'NON_RESIDENT_OWNER') return 'Non Residing Owner'

  const role = getInviteValue(invite, 'ROLE', 'role').toUpperCase()
  const userType = getInviteValue(invite, 'USER_TYPE', 'userType', 'user_type').toUpperCase()
  if (role === 'NON-RESIDENT' && userType === 'OWNER') return 'Non Residing Owner'
  if (role === 'RESIDENT' && userType === 'TENANT') return 'Resident Tenant'
  if (role === 'RESIDENT' && userType === 'OWNER') return 'Resident Owner'
  return '—'
}

function getResidentTypeCategory(invite) {
  const residentType = getInviteValue(invite, 'RESIDENT_TYPE', 'residentType', 'resident_type').toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
  if (residentType === 'RESIDENT_TENANT') return 'tenant'
  if (residentType === 'RESIDENT_OWNER') return 'resident_owner'
  if (residentType === 'NON_RESIDING_OWNER' || residentType === 'NON_RESIDENT_OWNER') return 'non_resident_owner'

  const role = getInviteValue(invite, 'ROLE', 'role').toUpperCase()
  const userType = getInviteValue(invite, 'USER_TYPE', 'userType', 'user_type').toUpperCase()
  if (role === 'NON-RESIDENT' && userType === 'OWNER') return 'non_resident_owner'
  if (role === 'RESIDENT' && userType === 'TENANT') return 'tenant'
  if (role === 'RESIDENT' && userType === 'OWNER') return 'resident_owner'
  return 'unknown'
}

function validateVehicleYear(value) {
  const year = Number(normalizeVehicleYear(value))
  return Number.isInteger(year) && year >= 1980 && year <= currentYear + 1
}

function emptyAddForm(phoneRegion, vehicleMakes) {
  return {
    firstName: '',
    lastName: '',
    email: '',
    mobileNbr: getPhonePrefix(phoneRegion),
    unitNbr: '',
    residentType: 'RESIDENT_OWNER',
    vehicleType: 'FOUR_WHEELER',
    vehicleNbr: '',
    vehicleMake: vehicleMakes[0] || 'Other',
    vehicleMakeOther: '',
    vehicleModel: '',
    vehicleYear: '',
  }
}

/**
 * ResidentManagement — Admin tab for inviting and managing residents.
 *
 * Props:
 *   communityId  {string}  — current community ID
 *   idToken      {string}  — Google id_token for auth
 *   featureFlags {object}  — optional FEATURE_FLAGS from community record
 */
export default function ResidentManagement({ communityId, idToken, featureFlags }) {
  const [invites, setInvites] = useState([])
  const [loadingList, setLoadingList] = useState(true)
  const [listError, setListError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isCompactView, setIsCompactView] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth <= 768
  })

  // Add resident modal
  const [showAddModal, setShowAddModal] = useState(false)
  const phoneRegion = detectPhoneRegion()
  const vehicleMakes = VEHICLE_MAKES_BY_REGION[phoneRegion] || VEHICLE_MAKES_BY_REGION.US
  const [addForm, setAddForm] = useState(() => emptyAddForm(phoneRegion, vehicleMakes))
  const [addLoading, setAddLoading] = useState(false)
  const [addError, setAddError] = useState('')
  const [addSuccess, setAddSuccess] = useState('')

  // Bulk upload modal
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [csvPreview, setCsvPreview] = useState([])
  const [csvError, setCsvError] = useState('')
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkResults, setBulkResults] = useState(null)
  const fileInputRef = useRef()

  // Load invite list
  function loadInvites() {
    if (!communityId) return
    setLoadingList(true)
    setListError('')
    listResidentInvites(communityId, idToken)
      .then(data => setInvites(Array.isArray(data) ? data : []))
      .catch(err => setListError(err.message || 'Failed to load residents'))
      .finally(() => setLoadingList(false))
  }

  useEffect(() => { loadInvites() }, [communityId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mediaQuery = window.matchMedia('(max-width: 768px)')
    const syncLayout = (event) => setIsCompactView(event.matches)
    setIsCompactView(mediaQuery.matches)
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncLayout)
      return () => mediaQuery.removeEventListener('change', syncLayout)
    }
    mediaQuery.addListener(syncLayout)
    return () => mediaQuery.removeListener(syncLayout)
  }, [])

  useEffect(() => {
    if (!showAddModal) return
    setAddForm((prev) => ({
      ...prev,
      mobileNbr: prev.mobileNbr || getPhonePrefix(phoneRegion),
      vehicleMake: prev.vehicleMake || vehicleMakes[0] || 'Other',
    }))
  }, [showAddModal, phoneRegion, vehicleMakes])

  // ── Add single resident ────────────────────────────────────────────────────

  async function handleAddResident(e) {
    e.preventDefault()
    setAddError('')
    setAddSuccess('')
    const { firstName, lastName, email, mobileNbr, unitNbr, residentType, vehicleType, vehicleNbr, vehicleMake, vehicleMakeOther, vehicleModel, vehicleYear } = addForm
    if (!validatePersonName(firstName)) {
      setAddError('Please enter a valid first name')
      return
    }
    if (lastName.trim() && !validatePersonName(lastName)) {
      setAddError('Please enter a valid last name')
      return
    }
    if (!validateEmail(email)) {
      setAddError('Please enter a valid email address')
      return
    }
    if (!validatePhone(mobileNbr, phoneRegion)) {
      setAddError(getPhoneValidationMessage(phoneRegion))
      return
    }
    const normalizedVehicleType = normalizeVehicleType(vehicleType)
    if (!normalizedVehicleType) {
      setAddError('Please select vehicle type')
      return
    }
    const requiresVehicleDetails = normalizedVehicleType !== 'NOT_APPLICABLE'
    const normalizedVehicleNbr = requiresVehicleDetails ? normalizeVehicleNumber(vehicleNbr) : ''
    if (requiresVehicleDetails && !validateVehicleNumber(normalizedVehicleNbr)) {
      setAddError('Please enter a valid vehicle number')
      return
    }
    const normalizedVehicleMake = requiresVehicleDetails ? normalizeVehicleText(vehicleMake === 'Other' ? vehicleMakeOther : vehicleMake) : ''
    if (requiresVehicleDetails && !normalizedVehicleMake) {
      setAddError('Please select or enter a vehicle make')
      return
    }
    const normalizedVehicleModel = requiresVehicleDetails ? normalizeVehicleText(vehicleModel) : ''
    if (requiresVehicleDetails && !normalizedVehicleModel) {
      setAddError('Please enter vehicle model')
      return
    }
    const normalizedVehicleYear = requiresVehicleDetails ? normalizeVehicleYear(vehicleYear) : ''
    if (requiresVehicleDetails && !validateVehicleYear(normalizedVehicleYear)) {
      setAddError(`Please enter a valid vehicle year between 1980 and ${currentYear + 1}`)
      return
    }
    if (!RESIDENT_TYPES.some((option) => option.value === residentType)) {
      setAddError('Please select resident type')
      return
    }
    setAddLoading(true)
    try {
      const normalizedEmail = normalizeEmail(email)
      const normalizedMobile = normalizePhone(mobileNbr, phoneRegion)
      await createResidentInvite({
        communityId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: normalizedEmail,
        mobileNbr: normalizedMobile,
        unitNbr: unitNbr.trim(),
        residentType,
        vehicleType: normalizedVehicleType,
        vehicleNbr: normalizedVehicleNbr,
        vehicleMake: normalizedVehicleMake,
        vehicleModel: normalizedVehicleModel,
        vehicleYear: normalizedVehicleYear,
      }, idToken)
      setAddSuccess(`${normalizedEmail} added successfully!`)
      setAddForm(emptyAddForm(phoneRegion, vehicleMakes))
      loadInvites()
    } catch (err) {
      setAddError(err.message || 'Failed to send invite')
    } finally {
      setAddLoading(false)
    }
  }

  // ── Bulk CSV upload ────────────────────────────────────────────────────────

  async function handleFileChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setCsvError('')
    setCsvPreview([])
    setBulkResults(null)
    try {
      const rows = await parseResidentCSV(file)
      if (rows.length === 0) {
        setCsvError('No valid rows found in CSV. Check the column headers.')
        return
      }
      setCsvPreview(rows)
    } catch (err) {
      setCsvError(err.message || 'Failed to parse CSV file')
    }
  }

  async function handleBulkSubmit() {
    if (csvPreview.length === 0) return
    setBulkLoading(true)
    setBulkResults(null)
    setCsvError('')
    try {
      for (let i = 0; i < csvPreview.length; i += 1) {
        const row = csvPreview[i]
        if (!validatePersonName(row.firstName)) {
          setCsvError(`Row ${i + 1}: valid first name is required`)
          setBulkLoading(false)
          return
        }
        if (row.lastName && !validatePersonName(row.lastName)) {
          setCsvError(`Row ${i + 1}: valid last name is required`)
          setBulkLoading(false)
          return
        }
        if (!validateEmail(row.email)) {
          setCsvError(`Row ${i + 1}: valid email address is required`)
          setBulkLoading(false)
          return
        }
        if (!validatePhone(row.mobileNbr, phoneRegion)) {
          setCsvError(`Row ${i + 1}: ${getPhoneValidationMessage(phoneRegion)}`)
          setBulkLoading(false)
          return
        }
        const normalizedResidentType = normalizeResidentTypeValue(row.residentType)
        if (!['RESIDENT_TENANT', 'RESIDENT_OWNER', 'NON_RESIDING_OWNER'].includes(normalizedResidentType)) {
          setCsvError(`Row ${i + 1}: resident_type must be RESIDENT_TENANT, RESIDENT_OWNER, or NON_RESIDING_OWNER`)
          setBulkLoading(false)
          return
        }
        const rowVehicleType = normalizeVehicleType(row.vehicleType)
        if (!rowVehicleType) {
          setCsvError(`Row ${i + 1}: vehicle_type must be TWO_WHEELER or FOUR_WHEELER`)
          setBulkLoading(false)
          return
        }
        if (!validateVehicleNumber(row.vehicleNbr)) {
          setCsvError(`Row ${i + 1}: valid vehicle_nbr is required`)
          setBulkLoading(false)
          return
        }
        if (!normalizeVehicleText(row.vehicleMake)) {
          setCsvError(`Row ${i + 1}: vehicle_make is required`)
          setBulkLoading(false)
          return
        }
        if (!normalizeVehicleText(row.vehicleModel)) {
          setCsvError(`Row ${i + 1}: vehicle_model is required`)
          setBulkLoading(false)
          return
        }
        if (!validateVehicleYear(row.vehicleYear)) {
          setCsvError(`Row ${i + 1}: vehicle_year must be between 1980 and ${currentYear + 1}`)
          setBulkLoading(false)
          return
        }
      }
      const residents = csvPreview.map(r => ({
        firstName: r.firstName.trim(),
        lastName: (r.lastName || '').trim(),
        email: normalizeEmail(r.email),
        mobileNbr: normalizePhone(r.mobileNbr, phoneRegion),
        unitNbr: r.unitNbr || '',
        residentType: normalizeResidentTypeValue(r.residentType),
        vehicleType: normalizeVehicleType(r.vehicleType),
        vehicleNbr: normalizeVehicleNumber(r.vehicleNbr),
        vehicleMake: normalizeVehicleText(r.vehicleMake),
        vehicleModel: normalizeVehicleText(r.vehicleModel),
        vehicleYear: normalizeVehicleYear(r.vehicleYear),
      }))
      const results = await bulkResidentInvite({ communityId, residents }, idToken)
      setBulkResults(normalizeBulkResults(results))
      setCsvPreview([])
      if (fileInputRef.current) fileInputRef.current.value = ''
      loadInvites()
    } catch (err) {
      setCsvError(err.message || 'Bulk invite failed')
    } finally {
      setBulkLoading(false)
    }
  }

  async function downloadCSVTemplate() {
    const [makeOne, makeTwo] = (VEHICLE_MAKES_BY_REGION[phoneRegion] || VEHICLE_MAKES_BY_REGION.US).filter((m) => m !== 'Other')
    const content = `first_name,last_name,email,mobile_nbr,unit_nbr,resident_type,vehicle_type,vehicle_nbr,vehicle_make,vehicle_model,vehicle_year\nJane,Doe,jane@example.com,${getPhoneExample(phoneRegion)},101,RESIDENT_OWNER,FOUR_WHEELER,AB12CD1234,${makeOne || 'Toyota'},Model X,2022\nJohn,Smith,john@example.com,${getPhoneExample(phoneRegion)},202,RESIDENT_TENANT,TWO_WHEELER,XY34EF5678,${makeTwo || 'Honda'},Model Y,2021\nAlice,Johnson,alice@example.com,${getPhoneExample(phoneRegion)},303,NON_RESIDING_OWNER,FOUR_WHEELER,CD34EF5678,${makeOne || 'Toyota'},Model Z,2023\n`
    const filename = 'resident_invite_template.csv'

    const isNative = typeof window !== 'undefined' && window.Capacitor &&
      (typeof window.Capacitor.isNativePlatform === 'function'
        ? window.Capacitor.isNativePlatform()
        : window.Capacitor.getPlatform?.() !== 'web')

    if (isNative) {
      try {
        const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem')
        const { Share } = await import('@capacitor/share')
        await Filesystem.writeFile({
          path: filename,
          data: content,
          directory: Directory.Cache,
          encoding: Encoding.UTF8,
        })
        const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache })
        await Share.share({ title: 'Resident Invite Template', url: uri, dialogTitle: 'Save or open template CSV' })
        return
      } catch (err) {
        console.error('Native CSV download failed, falling back to web:', err)
      }
    }

    // Web (and native fallback): blob URL
    const blob = new Blob([content], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const maxResidents = featureFlags?.max_residents ?? null
  const atLimit = maxResidents !== null && invites.length >= maxResidents

  const summaryCounts = useMemo(() => {
    const counts = { residentOwner: 0, nonResidentOwner: 0, tenant: 0, unknown: 0 }
    invites.forEach((invite) => {
      const category = getResidentTypeCategory(invite)
      if (category === 'resident_owner') counts.residentOwner += 1
      else if (category === 'non_resident_owner') counts.nonResidentOwner += 1
      else if (category === 'tenant') counts.tenant += 1
      else counts.unknown += 1
    })
    return counts
  }, [invites])

  const filteredInvites = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) return []
    return invites.filter((invite) => {
      const unit = getInviteValue(invite, 'UNIT_NBR', 'unitNbr', 'unit_nbr').toLowerCase()
      const firstName = getInviteValue(invite, 'FIRST_NAME', 'firstName', 'first_name').toLowerCase()
      const lastName = getInviteValue(invite, 'LAST_NAME', 'lastName', 'last_name').toLowerCase()
      const email = getInviteValue(invite, 'EMAIL', 'email').toLowerCase()
      return unit.includes(normalizedQuery) || `${firstName} ${lastName}`.includes(normalizedQuery) || email.includes(normalizedQuery)
    })
  }, [invites, searchQuery])

  const groupedFilteredInvites = useMemo(() => {
    const groups = new Map()
    filteredInvites.forEach((invite) => {
      const unit = getInviteValue(invite, 'UNIT_NBR', 'unitNbr', 'unit_nbr') || 'Unassigned'
      const bucket = groups.get(unit) || []
      bucket.push(invite)
      groups.set(unit, bucket)
    })
    return Array.from(groups.entries())
      .map(([unit, residents]) => ({ unit, residents }))
      .sort((a, b) => a.unit.localeCompare(b.unit))
  }, [filteredInvites])

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.title}>Residents</h2>
          <p style={styles.subtitle}>
            {invites.length} resident{invites.length !== 1 ? 's' : ''} added
            {maxResidents !== null ? ` · Limit: ${maxResidents}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', width: isCompactView ? '100%' : 'auto' }}>
          <button
            style={{ ...styles.btnSecondary, width: isCompactView ? '100%' : 'auto' }}
            onClick={() => { setShowBulkModal(true); setBulkResults(null); setCsvPreview([]); setCsvError('') }}
          >
            📁 Bulk Upload
          </button>
          <button
            style={{ ...styles.btnPrimary, opacity: atLimit ? 0.5 : 1, width: isCompactView ? '100%' : 'auto' }}
            disabled={atLimit}
            onClick={() => { setShowAddModal(true); setAddError(''); setAddSuccess('') }}
          >
            + Add Resident
          </button>
        </div>
      </div>

      {atLimit && (
        <div style={styles.limitBanner}>
          ⚠️ You've reached the resident limit ({maxResidents}) on your current plan. Upgrade to add more.
        </div>
      )}

      <div style={styles.searchPanel}>
        <div style={styles.searchHeaderRow}>
          <div>
            <div style={styles.searchTitle}>Search by unit / apartment</div>
            <div style={styles.searchSubtitle}>Enter an apartment or unit number to view the residents tied to that home.</div>
          </div>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="e.g. 101 or A-12"
            style={{ ...styles.searchInput, width: isCompactView ? '100%' : 'auto', minWidth: isCompactView ? 0 : 240 }}
          />
        </div>
        <div style={{ ...styles.summaryGrid, gridTemplateColumns: isCompactView ? 'repeat(2, minmax(0, 1fr))' : styles.summaryGrid.gridTemplateColumns }}>
          <SummaryCard label="Resident Owners" count={summaryCounts.residentOwner} />
          <SummaryCard label="Non-Resident Owners" count={summaryCounts.nonResidentOwner} />
          <SummaryCard label="Tenants" count={summaryCounts.tenant} />
          <SummaryCard label="Other / Unknown" count={summaryCounts.unknown} />
        </div>
      </div>

      {/* Invite table */}
      {loadingList ? (
        <p style={{ color: '#6b7280', padding: 24 }}>Loading…</p>
      ) : listError ? (
        <p style={{ color: '#dc2626', padding: 24 }}>{listError}</p>
      ) : invites.length === 0 ? (
        <EmptyState onAdd={() => setShowAddModal(true)} />
      ) : !searchQuery ? (
        <div style={styles.emptySearchState}>
          Search by unit or apartment number to view residents for that home.
        </div>
      ) : groupedFilteredInvites.length === 0 ? (
        <div style={styles.emptySearchState}>No residents found for “{searchQuery}”.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {groupedFilteredInvites.map(({ unit, residents }) => (
            <div key={unit} style={styles.unitSection}>
              <div style={styles.unitSectionHeader}>
                <span style={styles.unitSectionTitle}>Unit / Apartment {unit}</span>
                <span style={styles.unitSectionMeta}>{residents.length} resident{residents.length !== 1 ? 's' : ''}</span>
              </div>
              {isCompactView ? <MobileResidentList invites={residents} /> : <DesktopResidentGrid invites={residents} />}
            </div>
          ))}
        </div>
      )}

      {/* ── Add Resident Modal ── */}
      {showAddModal && (
        <Modal title="Add Resident" onClose={() => setShowAddModal(false)} scrollOnDesktop>
          {addSuccess ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <p style={{ color: '#16a34a', fontWeight: 600 }}>{addSuccess}</p>
              <button style={styles.btnPrimary} onClick={() => { setShowAddModal(false); setAddSuccess('') }}>Done</button>
              <button style={{ ...styles.btnSecondary, marginTop: 8 }} onClick={() => { setAddSuccess(''); setAddError('') }}>
                Add Another
              </button>
            </div>
          ) : (
            <form onSubmit={handleAddResident} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ ...styles.formRow, flexDirection: isCompactView ? 'column' : 'row' }}>
                <Field label="First Name *" value={addForm.firstName} onChange={v => setAddForm(p => ({ ...p, firstName: v }))} placeholder="Jane" autoComplete="given-name" />
                <Field label="Last Name" value={addForm.lastName} onChange={v => setAddForm(p => ({ ...p, lastName: v }))} placeholder="Doe" autoComplete="family-name" />
              </div>
              <Field label="Email *" type="email" value={addForm.email} onChange={v => setAddForm(p => ({ ...p, email: v }))} placeholder="jane@example.com" autoComplete="email" />
              <Field label="Mobile Number *" type="tel" value={addForm.mobileNbr} onChange={v => setAddForm(p => ({ ...p, mobileNbr: v }))} placeholder={getPhoneExample(phoneRegion)} inputMode="tel" autoComplete="tel" />
              <Field label="Unit Number" value={addForm.unitNbr} onChange={v => setAddForm(p => ({ ...p, unitNbr: v }))} placeholder="101" inputMode="numeric" autoComplete="off" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={styles.label}>Resident Type *</label>
                <select
                  value={addForm.residentType}
                  onChange={e => setAddForm(p => ({ ...p, residentType: e.target.value }))}
                  style={styles.input}
                >
                  {RESIDENT_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ ...styles.formRow, flexDirection: isCompactView ? 'column' : 'row' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={styles.label}>Vehicle Type *</label>
                  <select value={addForm.vehicleType} onChange={e => setAddForm(p => ({ ...p, vehicleType: e.target.value }))} style={styles.input}>
                    {VEHICLE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                {!isVehicleNotApplicable(addForm.vehicleType) && (
                  <Field label="Vehicle Number *" value={addForm.vehicleNbr} onChange={v => setAddForm(p => ({ ...p, vehicleNbr: v.toUpperCase() }))} placeholder="e.g. KA01AB1234" autoComplete="off" />
                )}
              </div>
              {!isVehicleNotApplicable(addForm.vehicleType) && (
                <div style={{ ...styles.formRow, flexDirection: isCompactView ? 'column' : 'row' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={styles.label}>Vehicle Make *</label>
                    <select value={addForm.vehicleMake} onChange={e => setAddForm(p => ({ ...p, vehicleMake: e.target.value }))} style={styles.input}>
                      {vehicleMakes.map((make) => (
                        <option key={make} value={make}>{make}</option>
                      ))}
                    </select>
                  </div>
                  {addForm.vehicleMake === 'Other' && (
                    <Field label="Other Make *" value={addForm.vehicleMakeOther} onChange={v => setAddForm(p => ({ ...p, vehicleMakeOther: v }))} placeholder="Enter make" autoComplete="off" />
                  )}
                </div>
              )}
              {!isVehicleNotApplicable(addForm.vehicleType) && (
                <div style={{ ...styles.formRow, flexDirection: isCompactView ? 'column' : 'row' }}>
                  <Field label="Vehicle Model *" value={addForm.vehicleModel} onChange={v => setAddForm(p => ({ ...p, vehicleModel: v }))} placeholder="e.g. Swift / Civic" autoComplete="off" />
                  <Field label="Year of Make *" value={addForm.vehicleYear} onChange={v => setAddForm(p => ({ ...p, vehicleYear: v }))} placeholder="e.g. 2022" inputMode="numeric" autoComplete="off" />
                </div>
              )}
              {addError && <p style={styles.error}>{addError}</p>}
              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexDirection: isCompactView ? 'column' : 'row' }}>
                <button type="submit" style={styles.btnPrimary} disabled={addLoading}>
                  {addLoading ? 'Adding…' : 'Add Resident'}
                </button>
                <button type="button" style={styles.btnSecondary} onClick={() => setShowAddModal(false)}>Cancel</button>
              </div>
            </form>
          )}
        </Modal>
      )}

      {/* ── Bulk Upload Modal ── */}
      {showBulkModal && (
        <Modal title="Bulk Upload Residents" onClose={() => setShowBulkModal(false)}>
          {bulkResults ? (
            <BulkResults results={bulkResults} onClose={() => setShowBulkModal(false)} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontSize: 12, color: '#6b7280' }}>
                Phone numbers are validated automatically based on your app region ({phoneRegion === 'IN' ? 'India' : 'USA'}).
              </p>
              <div style={styles.csvTip}>
                <span>📄 Upload a CSV file with resident details.</span>
                <button
                  style={styles.linkBtn}
                  onClick={downloadCSVTemplate}
                  onTouchEnd={e => { e.preventDefault(); downloadCSVTemplate() }}
                >⬇ Download template</button>
              </div>
              <p style={{ fontSize: 12, color: '#6b7280' }}>
                Required columns: <code>first_name</code>, <code>email</code>, <code>mobile_nbr</code>, <code>resident_type</code>, <code>vehicle_type</code>, <code>vehicle_nbr</code>, <code>vehicle_make</code>, <code>vehicle_model</code>, <code>vehicle_year</code><br />
                Optional: <code>last_name</code>, <code>unit_nbr</code>
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                style={{ fontSize: 13 }}
              />
              {csvError && <p style={styles.error}>{csvError}</p>}
              {csvPreview.length > 0 && (
                <>
                  <p style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Preview ({csvPreview.length} rows)</p>
                  {isCompactView ? (
                    <MobileCsvPreview rows={csvPreview} />
                  ) : (
                    <div style={{ overflowX: 'auto', maxHeight: 240, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      <table style={{ ...styles.table, fontSize: 12 }}>
                        <thead>
                          <tr style={styles.tableHead}>
                            <th style={styles.th}>First Name</th>
                            <th style={styles.th}>Last Name</th>
                            <th style={styles.th}>Email</th>
                            <th style={styles.th}>Mobile</th>
                            <th style={styles.th}>Unit</th>
                            <th style={styles.th}>Vehicle Type</th>
                            <th style={styles.th}>Vehicle Number</th>
                            <th style={styles.th}>Vehicle Make</th>
                            <th style={styles.th}>Vehicle Model</th>
                            <th style={styles.th}>Vehicle Year</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreview.map((r, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={styles.td}>{r.firstName}</td>
                              <td style={styles.td}>{r.lastName || '—'}</td>
                              <td style={styles.td}>{r.email}</td>
                              <td style={styles.td}>{r.mobileNbr || '—'}</td>
                              <td style={styles.td}>{r.unitNbr || '—'}</td>
                              <td style={styles.td}>{r.vehicleType || '—'}</td>
                              <td style={styles.td}>{r.vehicleNbr || '—'}</td>
                              <td style={styles.td}>{r.vehicleMake || '—'}</td>
                              <td style={styles.td}>{r.vehicleModel || '—'}</td>
                              <td style={styles.td}>{r.vehicleYear || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, flexDirection: isCompactView ? 'column' : 'row' }}>
                    <button style={styles.btnPrimary} onClick={handleBulkSubmit} disabled={bulkLoading}>
                      {bulkLoading ? 'Adding…' : `Add ${csvPreview.length} Resident${csvPreview.length !== 1 ? 's' : ''}`}
                    </button>
                    <button style={styles.btnSecondary} onClick={() => { setCsvPreview([]); if (fileInputRef.current) fileInputRef.current.value = '' }}>
                      Clear
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ label, count }) {
  return (
    <div style={styles.summaryCard}>
      <div style={styles.summaryCardValue}>{count}</div>
      <div style={styles.summaryCardLabel}>{label}</div>
    </div>
  )
}

function EmptyState({ onAdd }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: '#6b7280' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
      <p style={{ fontWeight: 600, color: '#374151' }}>No residents added yet</p>
      <p style={{ fontSize: 13, marginBottom: 20 }}>Add residents individually or upload a CSV file.</p>
      <button style={styles.btnPrimary} onClick={onAdd}>+ Add First Resident</button>
    </div>
  )
}

function DesktopResidentGrid({ invites }) {
  return (
    <div style={styles.desktopGrid}>
      {invites.map((inv, index) => {
        const fullName = [getInviteValue(inv, 'FIRST_NAME', 'firstName', 'first_name'), getInviteValue(inv, 'LAST_NAME', 'lastName', 'last_name')].filter(Boolean).join(' ') || '—'
        const residentTypeLabel = getResidentTypeLabel(inv)
        return (
          <div key={getInviteValue(inv, 'EMAIL', 'email') || index} style={styles.desktopCard}>
            <div style={styles.desktopCardHeader}>
              <div>
                <div style={styles.mobileResidentName}>{fullName}</div>
                <div style={styles.mobileResidentEmail}>{getInviteValue(inv, 'EMAIL', 'email') || '—'}</div>
              </div>
              <StatusBadge status={getInviteValue(inv, 'STATUS', 'status')} />
            </div>
            <div style={styles.desktopInfoGrid}>
              <MobileInfoRow label="Mobile" value={getInviteValue(inv, 'MOBILE_NBR', 'mobileNbr', 'mobile_nbr')} />
              <MobileInfoRow label="Resident Type" value={residentTypeLabel} />
              <MobileInfoRow label="Unit" value={getInviteValue(inv, 'UNIT_NBR', 'unitNbr', 'unit_nbr')} />
              <MobileInfoRow label="Vehicle Type" value={getInviteValue(inv, 'VEHICLE_TYPE', 'vehicleType', 'vehicle_type')} />
              <MobileInfoRow label="Vehicle #" value={getInviteValue(inv, 'VEHICLE_NBR', 'vehicleNbr', 'vehicle_nbr')} />
              <MobileInfoRow label="Make" value={getInviteValue(inv, 'VEHICLE_MAKE', 'vehicleMake', 'vehicle_make')} />
              <MobileInfoRow label="Model" value={getInviteValue(inv, 'VEHICLE_MODEL', 'vehicleModel', 'vehicle_model')} />
              <MobileInfoRow label="Year" value={getInviteValue(inv, 'VEHICLE_YEAR', 'vehicleYear', 'vehicle_year')} />
              <MobileInfoRow label="Invited" value={getInviteValue(inv, 'INVITED_AT', 'CREATED_AT', 'createdAt') ? new Date(getInviteValue(inv, 'INVITED_AT', 'CREATED_AT', 'createdAt')).toLocaleDateString() : '—'} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function MobileResidentList({ invites }) {
  return (
    <div style={styles.mobileList}>
      {invites.map((inv, index) => {
        const fullName = [getInviteValue(inv, 'FIRST_NAME', 'firstName', 'first_name'), getInviteValue(inv, 'LAST_NAME', 'lastName', 'last_name')].filter(Boolean).join(' ') || '—'
        const residentTypeLabel = getResidentTypeLabel(inv)
        return (
          <div key={getInviteValue(inv, 'EMAIL', 'email') || index} style={styles.mobileCard}>
            <div style={styles.mobileCardHeader}>
              <div>
                <div style={styles.mobileResidentName}>{fullName}</div>
                <div style={styles.mobileResidentEmail}>{getInviteValue(inv, 'EMAIL', 'email') || '—'}</div>
              </div>
              <StatusBadge status={getInviteValue(inv, 'STATUS', 'status')} />
            </div>
            <div style={styles.mobileInfoGrid}>
              <MobileInfoRow label="Mobile" value={getInviteValue(inv, 'MOBILE_NBR', 'mobileNbr', 'mobile_nbr')} />
              <MobileInfoRow label="Resident Type" value={residentTypeLabel} />
              <MobileInfoRow label="Unit" value={getInviteValue(inv, 'UNIT_NBR', 'unitNbr', 'unit_nbr')} />
              <MobileInfoRow label="Vehicle Type" value={getInviteValue(inv, 'VEHICLE_TYPE', 'vehicleType', 'vehicle_type')} />
              <MobileInfoRow label="Vehicle #" value={getInviteValue(inv, 'VEHICLE_NBR', 'vehicleNbr', 'vehicle_nbr')} />
              <MobileInfoRow label="Make" value={getInviteValue(inv, 'VEHICLE_MAKE', 'vehicleMake', 'vehicle_make')} />
              <MobileInfoRow label="Model" value={getInviteValue(inv, 'VEHICLE_MODEL', 'vehicleModel', 'vehicle_model')} />
              <MobileInfoRow label="Year" value={getInviteValue(inv, 'VEHICLE_YEAR', 'vehicleYear', 'vehicle_year')} />
              <MobileInfoRow label="Invited" value={getInviteValue(inv, 'INVITED_AT', 'CREATED_AT', 'createdAt') ? new Date(getInviteValue(inv, 'INVITED_AT', 'CREATED_AT', 'createdAt')).toLocaleDateString() : '—'} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function MobileCsvPreview({ rows }) {
  return (
    <div style={styles.mobilePreviewList}>
      {rows.map((row, index) => (
        <div key={index} style={styles.mobilePreviewCard}>
          <div style={styles.mobilePreviewNameRow}>
            <span style={styles.mobileResidentName}>{[row.firstName, row.lastName].filter(Boolean).join(' ') || row.firstName || '—'}</span>
          </div>
          <div style={styles.mobileInfoGrid}>
            <MobileInfoRow label="Email" value={row.email} />
            <MobileInfoRow label="Mobile" value={row.mobileNbr} />
            <MobileInfoRow label="Unit" value={row.unitNbr} />
            <MobileInfoRow label="Type" value={row.vehicleType} />
            <MobileInfoRow label="Vehicle #" value={row.vehicleNbr} />
            <MobileInfoRow label="Make" value={row.vehicleMake} />
            <MobileInfoRow label="Model" value={row.vehicleModel} />
            <MobileInfoRow label="Year" value={row.vehicleYear} />
          </div>
        </div>
      ))}
    </div>
  )
}

function MobileInfoRow({ label, value }) {
  return (
    <div style={styles.mobileInfoRow}>
      <span style={styles.mobileInfoLabel}>{label}</span>
      <span style={styles.mobileInfoValue}>{value || '—'}</span>
    </div>
  )
}

function StatusBadge({ status }) {
  const cfg = {
    ACTIVE:      { bg: '#dcfce7', color: '#166534', label: 'Active' },
    PENDING:     { bg: '#fef9c3', color: '#854d0e', label: 'Pending' },
    REGISTERED:  { bg: '#dcfce7', color: '#166534', label: 'Registered' },
  }[status] || { bg: '#f3f4f6', color: '#6b7280', label: status }
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600,
    }}>
      {cfg.label}
    </span>
  )
}

function Field({ label, value, onChange, type = 'text', placeholder = '', inputMode, autoComplete }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={styles.label}>{label}</label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        inputMode={inputMode}
        autoComplete={autoComplete}
        style={styles.input}
      />
    </div>
  )
}



function BulkResults({ results, onClose }) {
  const normalizedResults = normalizeBulkResults(results)
  const succeeded = normalizedResults.filter(r => r.success)
  const failed = normalizedResults.filter(r => !r.success)
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1, background: '#dcfce7', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#166534' }}>{succeeded.length}</div>
          <div style={{ fontSize: 12, color: '#166534' }}>Invited</div>
        </div>
        <div style={{ flex: 1, background: failed.length ? '#fef2f2' : '#f3f4f6', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: failed.length ? '#991b1b' : '#6b7280' }}>{failed.length}</div>
          <div style={{ fontSize: 12, color: failed.length ? '#991b1b' : '#6b7280' }}>Failed</div>
        </div>
      </div>
      {failed.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Failed entries:</p>
          {failed.map((r, i) => (
            <div key={i} style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: '6px 10px', borderRadius: 6, marginBottom: 4 }}>
              {r.email} — {r.error || 'Unknown error'}
            </div>
          ))}
        </div>
      )}
      <button style={styles.btnPrimary} onClick={onClose}>Done</button>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 16 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 },
  subtitle: { fontSize: 13, color: '#6b7280', margin: '2px 0 0' },
  limitBanner: {
    background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8,
    padding: '10px 14px', fontSize: 13, color: '#92400e',
  },
  searchPanel: {
    background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
  },
  searchHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  searchTitle: { fontSize: 14, fontWeight: 700, color: '#111827' },
  searchSubtitle: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  searchInput: {
    padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 14, minWidth: 240, outline: 'none', boxSizing: 'border-box',
  },
  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10,
  },
  summaryCard: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px',
  },
  summaryCardValue: { fontSize: 22, fontWeight: 700, color: '#1d4ed8' },
  summaryCardLabel: { fontSize: 12, color: '#6b7280', marginTop: 4 },
  emptySearchState: {
    background: '#fff', border: '1px dashed #d1d5db', borderRadius: 12, padding: '20px 16px', textAlign: 'center', color: '#6b7280', fontSize: 13,
  },
  unitSection: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 12,
  },
  unitSectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  },
  unitSectionTitle: { fontSize: 14, fontWeight: 700, color: '#111827' },
  unitSectionMeta: { fontSize: 12, color: '#6b7280' },
  desktopGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 12,
  },
  desktopCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '14px 16px',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
  },
  desktopCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  desktopInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff' },
  mobileList: { display: 'flex', flexDirection: 'column', gap: 12 },
  mobileCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '14px 16px',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
  },
  mobileCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  mobileResidentName: { fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.3 },
  mobileResidentEmail: { fontSize: 12, color: '#6b7280', marginTop: 4, wordBreak: 'break-word' },
  mobileInfoGrid: { display: 'grid', gap: 10 },
  mobileInfoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  mobileInfoLabel: { fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' },
  mobileInfoValue: { fontSize: 13, color: '#374151', textAlign: 'right', wordBreak: 'break-word' },
  mobilePreviewList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: 320,
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 10,
    background: '#f9fafb',
  },
  mobilePreviewCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '12px 14px',
  },
  mobilePreviewNameRow: {
    marginBottom: 10,
  },
  tableHead: { background: '#f9fafb' },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '10px 14px', fontSize: 13, color: '#374151' },
  btnPrimary: {
    background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  btnSecondary: {
    background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8,
    padding: '10px 18px', fontSize: 14, cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modalCard: {
    background: '#fff', borderRadius: 12, padding: 24,
    width: '100%', maxWidth: 520,
    maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  formRow: { display: 'flex', gap: 12 },
  label: { fontSize: 13, fontWeight: 500, color: '#374151' },
  input: {
    padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 7,
    fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box',
  },
  error: { fontSize: 13, color: '#dc2626', background: '#fef2f2', padding: '8px 12px', borderRadius: 6 },
  csvTip: { fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 },
  linkBtn: { background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: 14, padding: '8px 4px', fontWeight: 600, minHeight: 44, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' },
}
